(() => {
  const config = window.PremiumProduct || {};
  const drawer = document.querySelector("#CartDrawer");
  const backdrop = document.querySelector("[data-cart-backdrop]");
  const countNodes = document.querySelectorAll("[data-cart-count]");
  const itemsNode = document.querySelector("[data-cart-items]");
  const subtotalNode = document.querySelector("[data-cart-subtotal]");
  const footerNode = document.querySelector("[data-cart-footer]");
  const announcementNode = document.querySelector("[data-cart-announcement]");
  const text = window.PremiumProductText || {};
  let previousFocus = null;

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (character) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    })[character]);

  const formatMoney = (amount) => {
    try {
      return new Intl.NumberFormat(config.locale || undefined, {
        style: "currency",
        currency: config.currency || "USD"
      }).format(Number(amount || 0) / 100);
    } catch {
      return (Number(amount || 0) / 100).toFixed(2);
    }
  };

  const setDrawer = (open) => {
    if (!drawer) return;
    if (open) previousFocus = document.activeElement;
    drawer.classList.toggle("is-open", open);
    drawer.setAttribute("aria-hidden", String(!open));
    drawer.inert = !open;
    if (backdrop) backdrop.hidden = !open;
    document.body.classList.toggle("cart-open", open);
    if (open) drawer.focus();
    else if (previousFocus?.isConnected) previousFocus.focus();
  };

  const renderCart = (cart) => {
    countNodes.forEach((node) => {
      node.textContent = String(cart.item_count || 0);
      node.setAttribute("aria-label", `${cart.item_count || 0} items`);
    });
    if (!itemsNode) return;
    if (!cart.items?.length) {
      itemsNode.innerHTML = `<p class="cart-drawer__empty">${escapeHtml(text.empty || "Your bag is empty.")}</p>`;
      if (footerNode) footerNode.hidden = true;
    } else {
      itemsNode.innerHTML = cart.items.map((item) => `
        <article class="cart-line" data-cart-key="${escapeHtml(item.key)}">
          <a class="cart-line__image" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="100" height="120">` : ""}
          </a>
          <div class="cart-line__info">
            <a href="${escapeHtml(item.url)}" class="cart-line__title">${escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== "Default Title" ? `<p class="text-small">${escapeHtml(item.variant_title)}</p>` : ""}
            <p class="text-small">${formatMoney(item.final_price)}</p>
            <div class="cart-line__controls">
              <label class="visually-hidden" for="drawer-qty-${escapeHtml(item.key)}">Quantity</label>
              <input id="drawer-qty-${escapeHtml(item.key)}" class="quantity-input" type="number" min="0" value="${Number(item.quantity)}" inputmode="numeric" data-drawer-quantity="${escapeHtml(item.key)}">
              <button class="text-link" type="button" data-remove-line="${escapeHtml(item.key)}">${escapeHtml(text.remove || "Remove")}</button>
            </div>
          </div>
        </article>
      `).join("");
      if (footerNode) footerNode.hidden = false;
    }
    if (subtotalNode) subtotalNode.textContent = formatMoney(cart.items_subtotal_price);
  };

  const getCart = async () => {
    if (!config.routes?.cartJson) return;
    const response = await fetch(config.routes.cartJson, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Cart could not be loaded");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch(config.routes.cartChange, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error("Cart could not be updated");
    renderCart(await response.json());
  };

  document.addEventListener("click", async (event) => {
    const lookButton = event.target.closest("[data-add-look]");
    if (lookButton) {
      const lookRoot = lookButton.closest("[data-shop-look]");
      const jsonNode = lookRoot?.parentElement?.querySelector("[data-look-products]") || lookRoot?.nextElementSibling;
      try {
        const products = JSON.parse(jsonNode?.textContent || "[]");
        const items = products.filter((item) => item.available).map((item) => ({ id: Number(item.variant), quantity: 1 }));
        if (!items.length || items.length !== products.length) throw new Error("Some pieces are unavailable.");
        lookButton.disabled = true;
        const response = await fetch(config.routes.cartAdd, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || text.addError || "Unable to add this look.");
        const cart = await getCart();
        if (announcementNode) announcementNode.textContent = text.addSuccess || "The look was added to your bag.";
        setDrawer(true);
        lookRoot.dispatchEvent(new CustomEvent("cart:look-added", { bubbles: true, detail: { result, cart } }));
      } catch (error) {
        const status = lookRoot?.querySelector("[data-look-status]");
        if (status) status.textContent = error.message || text.addError || "We couldn't add that look.";
        if (announcementNode) announcementNode.textContent = error.message || text.addError || "We couldn't add that look.";
      } finally {
        lookButton.disabled = false;
      }
      return;
    }
    const openButton = event.target.closest("[data-open-cart]");
    if (openButton && drawer) {
      event.preventDefault();
      try { await getCart(); } catch { /* Keep the normal cart link available. */ }
      setDrawer(true);
      return;
    }
    if (event.target.closest("[data-close-cart]") || event.target.matches("[data-cart-backdrop]")) {
      setDrawer(false);
      return;
    }
    const removeButton = event.target.closest("[data-remove-line]");
    if (removeButton) {
      try { await changeLine(removeButton.dataset.removeLine, 0); } catch { await getCart().catch(() => {}); }
    }
  });

  document.addEventListener("change", async (event) => {
    const input = event.target.closest("[data-drawer-quantity]");
    if (input) {
      try { await changeLine(input.dataset.drawerQuantity, Math.max(0, Number(input.value) || 0)); }
      catch { await getCart().catch(() => {}); }
    }
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest("form.product-form, form.product-card__quick-add");
    if (!form) return;
    if (form.matches("form.product-form") && !event.submitter?.matches("[data-product-submit]")) return;
    event.preventDefault();
    const button = event.submitter?.matches('[type="submit"]') ? event.submitter : form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    const originalText = button?.textContent;
    if (button) { button.disabled = true; button.setAttribute("aria-busy", "true"); }
    try {
      const formData = new FormData(form);
      formData.delete("return_to");
      const response = await fetch(config.routes.cartAdd, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || "Unable to add item");
      const cart = await getCart();
      if (announcementNode) announcementNode.textContent = `${result.product_title || "Item"} ${text.addSuccess || "added to your bag."}`;
      setDrawer(true);
      form.dispatchEvent(new CustomEvent("cart:item-added", { bubbles: true, detail: { item: result, cart } }));
    } catch (error) {
      if (announcementNode) announcementNode.textContent = error.message || text.addError || "We couldn't add that item. Please try again.";
      if (button) button.textContent = error.message || text.addError || "Try again";
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        window.setTimeout(() => { if (button.isConnected) button.textContent = originalText; }, 2200);
      }
    }
  });

  document.addEventListener("click", (event) => {
    const thumb = event.target.closest("[data-gallery-image]");
    if (!thumb) return;
    const scope = thumb.closest("[data-product-root]");
    const main = scope?.querySelector("[data-gallery-main]");
    const image = thumb.dataset.galleryImage;
    if (!scope) return;
    if (main && image) {
      main.innerHTML = `<img src="${escapeHtml(image)}" alt="${escapeHtml(thumb.dataset.galleryAlt)}" loading="eager">`;
      scope.querySelectorAll("[data-gallery-image]").forEach((node) => node.removeAttribute("aria-current"));
      thumb.setAttribute("aria-current", "true");
    }
  });

  const stickyBar = document.querySelector("[data-sticky-purchase]");
  const productForm = document.querySelector("form.product-form");
  if (stickyBar && productForm && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle("is-visible", !entry.isIntersecting);
    }, { threshold: 0.05 });
    observer.observe(productForm);
    stickyBar.querySelector("[data-sticky-add]")?.addEventListener("click", () => productForm.querySelector('[data-product-submit]')?.click());
  }

  document.querySelectorAll("[data-product-root]").forEach((scope) => {
    const recentNode = scope.querySelector("[data-recent-product]");
    if (recentNode) {
      try {
        const current = JSON.parse(recentNode.textContent || "{}");
        const history = JSON.parse(window.localStorage.getItem("premium-product-viewed-v1") || "[]");
        if (current.handle && current.url && Array.isArray(history)) {
          const next = [{ handle: current.handle, url: current.url }, ...history.filter((item) => item?.handle !== current.handle)].slice(0, 8);
          window.localStorage.setItem("premium-product-viewed-v1", JSON.stringify(next));
        }
      } catch { /* Recently viewed is optional when browser storage is unavailable. */ }
    }
    const jsonNode = scope.querySelector("[data-product-variants]");
    if (!jsonNode) return;
    let variants;
    try { variants = JSON.parse(jsonNode.textContent || "[]"); } catch { return; }
    const optionSelects = [...scope.querySelectorAll("[data-product-option]")];
    if (!optionSelects.length) return;
    const updateVariant = () => {
      const selected = optionSelects.map((select) => select.value);
      const variant = variants.find((candidate) =>
        Array.isArray(candidate.options) && candidate.options.every((value, index) => value === selected[index])
      );
      const idInput = scope.querySelector('[name="id"]');
      const addButton = scope.querySelector("[data-product-submit]");
      const status = scope.querySelector("[data-variant-status]");
      if (!variant) {
        if (idInput) idInput.value = "";
        if (addButton) { addButton.disabled = true; addButton.textContent = text.unavailable || "Unavailable"; }
        if (status) status.textContent = text.unavailable || "Unavailable";
      const stickyButton = document.querySelector("[data-sticky-add]");
      if (stickyButton) stickyButton.disabled = true;
        return;
      }
      if (idInput) idInput.value = String(variant.id);
      const available = variant.available === true;
      const price = scope.querySelector("[data-selected-price]");
      const compare = scope.querySelector("[data-selected-compare]");
      if (price) price.textContent = formatMoney(variant.price);
      if (compare) {
        const comparePrice = Number(variant.compare_at_price || 0);
        compare.hidden = comparePrice <= Number(variant.price || 0);
        compare.textContent = formatMoney(comparePrice);
      }
      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available ? (addButton.dataset.addLabel || text.addLabel || "Add to bag") : (addButton.dataset.soldLabel || text.soldOut || "Sold out");
      }
      if (status) status.textContent = available ? (text.available || "Available") : (text.soldOut || "Sold out");
      const skuWrapper = scope.querySelector("[data-selected-sku-wrapper]");
      const sku = scope.querySelector("[data-selected-sku]");
      if (sku) sku.textContent = variant.sku || "";
      if (skuWrapper) skuWrapper.hidden = !variant.sku;
      const stickyButton = document.querySelector("[data-sticky-add]");
      if (stickyButton) stickyButton.disabled = !available;
      const stickyTitle = document.querySelector("[data-sticky-title]");
      if (stickyTitle) stickyTitle.textContent = `${scope.querySelector("h1")?.textContent || ""} · ${formatMoney(variant.price)}`;
      if (variant.featured_image?.src) {
        const main = scope.querySelector("[data-gallery-main]");
        if (main) main.innerHTML = `<img src="${escapeHtml(variant.featured_image.src)}" alt="" loading="eager">`;
      }
    };
    optionSelects.forEach((select) => select.addEventListener("change", updateVariant));
    updateVariant();
  });

  document.querySelectorAll("[data-recently-viewed]").forEach(async (root) => {
    const grid = root.querySelector("[data-recently-viewed-grid]");
    if (!grid) return;
    let history = [];
    try { history = JSON.parse(window.localStorage.getItem("premium-product-viewed-v1") || "[]"); } catch { return; }
    if (!Array.isArray(history)) return;
    const recent = history.filter((item) => item?.handle && item?.url).slice(1, 5);
    const products = await Promise.all(recent.map(async (item) => {
      try {
        const endpoint = new URL(`${item.url.replace(/\/$/, "")}.js`, window.location.origin);
        if (endpoint.origin !== window.location.origin) return null;
        const response = await fetch(endpoint.href, { headers: { Accept: "application/json" } });
        if (!response.ok) return null;
        return await response.json();
      } catch { return null; }
    }));
    products.filter((product) => product?.url && product?.title).forEach((product) => {
      const card = document.createElement("article");
      card.className = "product-card";
      const link = document.createElement("a");
      link.className = "product-card__media";
      link.href = product.url;
      link.setAttribute("aria-label", product.title);
      if (product.featured_image) {
        const image = document.createElement("img");
        image.src = product.featured_image;
        image.alt = product.title;
        image.loading = "lazy";
        link.append(image);
      }
      const content = document.createElement("div");
      content.className = "product-card__content";
      const title = document.createElement("h3");
      title.className = "product-card__title";
      const titleLink = document.createElement("a");
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      title.append(titleLink);
      const price = document.createElement("span");
      price.className = "price";
      price.textContent = formatMoney(product.price);
      content.append(title, price);
      card.append(link, content);
      grid.append(card);
    });
    if (grid.childElementCount) root.hidden = false;
  });

  document.querySelectorAll("[data-shop-look]").forEach((root) => {
    const script = root.parentElement?.querySelector("[data-look-products]") || root.nextElementSibling;
    let products = [];
    try { products = JSON.parse(script?.textContent || "[]"); } catch { /* Leave theme-editor settings intact. */ }
    const totalNode = root.querySelector("[data-look-total]");
    const button = root.querySelector("[data-add-look]");
    const statusNode = root.querySelector("[data-look-status]");
    const total = products.reduce((sum, product) => sum + Number(product.price || 0), 0);
    if (totalNode) totalNode.textContent = formatMoney(total);
    const unavailable = products.some((product) => !product.available);
    if (button) button.disabled = !products.length || unavailable;
    if (statusNode && unavailable) statusNode.textContent = text.soldOut || "One or more selected items are unavailable.";
    root.querySelectorAll("[data-look-hotspot]").forEach((hotspot) => {
      hotspot.addEventListener("click", () => {
        root.querySelectorAll("[data-look-hotspot]").forEach((node) => node.setAttribute("aria-pressed", String(node === hotspot)));
        root.querySelectorAll("[data-look-item]").forEach((node) => node.classList.toggle("is-highlighted", node.dataset.lookItem === hotspot.dataset.lookHotspot));
      });
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawer?.classList.contains("is-open")) setDrawer(false);
    if (event.key !== "Tab" || !drawer?.classList.contains("is-open")) return;
    const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')];
    if (!focusable.length) return;
    if (event.shiftKey && document.activeElement === focusable[0]) {
      event.preventDefault();
      focusable[focusable.length - 1].focus();
    } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
      event.preventDefault();
      focusable[0].focus();
    }
  });

  if (config.routes?.cartJson) getCart().catch(() => {});
})();
